import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Zap, ChevronRight } from "lucide-react";
import { getFeatureFlags } from "@/lib/admin/queries";
import { updateFeatureFlag } from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link } from "react-router-dom";

const FLAG_DEFINITIONS: {
  key: string;
  label: string;
  description: string;
  category: string;
  risk: "low" | "medium" | "high";
}[] = [
  {
    key: "guest_portal_enabled",
    label: "Guest Portal",
    description: "Guest portal accessible without login via public token-based URLs",
    category: "Access",
    risk: "low",
  },
  {
    key: "maintenance_requests_enabled",
    label: "Maintenance Requests",
    description: "Maintenance request tracking for units and buildings",
    category: "Features",
    risk: "low",
  },
  {
    key: "dispute_tracking_enabled",
    label: "Dispute Tracking",
    description: "Dispute resolution module for booking and compliance conflicts",
    category: "Features",
    risk: "low",
  },
  {
    key: "income_tracker_enabled",
    label: "Income Tracker",
    description: "Income & expense tracking feature (Individual plan only)",
    category: "Features",
    risk: "low",
  },
  {
    key: "cross_building_reports_enabled",
    label: "Cross-Building Reports",
    description: "Cross-building reports and analytics (Property Manager plan only)",
    category: "Reports",
    risk: "low",
  },
  {
    key: "agm_export_enabled",
    label: "AGM Report Export",
    description: "AGM-ready CSV/PDF report exports for compliance data",
    category: "Reports",
    risk: "low",
  },
  {
    key: "new_registrations_open",
    label: "New Registrations",
    description: "Allow new user signups and account creation on the platform",
    category: "Access",
    risk: "high",
  },
  {
    key: "maintenance_mode",
    label: "Maintenance Mode",
    description: "Show maintenance page to all non-admin users — disables platform access",
    category: "Platform",
    risk: "high",
  },
];

const RISK_STYLES: Record<string, string> = {
  low: "bg-green-50 text-green-700 border-green-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  high: "bg-red-50 text-red-700 border-red-200",
};

export default function SettingsFeatures() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const flagsQuery = useQuery({
    queryKey: ["admin", "feature-flags"],
    queryFn: getFeatureFlags,
  });

  const flagsData: Record<string, unknown>[] = Array.isArray(flagsQuery.data?.data)
    ? flagsQuery.data!.data
    : [];

  const getFlagRecord = (key: string) =>
    flagsData.find((f) => (f.flag_name as string) === key);

  const getFlagValue = (key: string): boolean =>
    (getFlagRecord(key)?.enabled as boolean) ?? false;

  const getFlagUpdatedAt = (key: string): string | null =>
    (getFlagRecord(key)?.updated_at as string) ?? null;

  const getFlagUpdatedBy = (key: string): string | null =>
    (getFlagRecord(key)?.updated_by_name as string) ?? null;

  const toggleMutation = useMutation({
    mutationFn: ({ flag, value }: { flag: string; value: boolean }) =>
      updateFeatureFlag(flag, value, actorId),
    onSuccess: (_, vars) => {
      toast.success(`"${vars.flag}" ${vars.value ? "enabled" : "disabled"}`);
      qc.invalidateQueries({ queryKey: ["admin", "feature-flags"] });
    },
    onError: (_, vars) => toast.error(`Failed to update "${vars.flag}"`),
  });

  const categories = [...new Set(FLAG_DEFINITIONS.map((f) => f.category))];

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/settings">Settings</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <ChevronRight className="h-3.5 w-3.5" />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>Feature Flags</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center gap-3">
        <Zap className="h-6 w-6 text-gray-600" />
        <h1 className="text-2xl font-bold text-gray-900">Feature Flags</h1>
      </div>

      <p className="text-sm text-gray-500 max-w-2xl">
        Toggle platform features on or off. High-risk flags affect platform-wide access — use with caution.
      </p>

      {flagsQuery.isLoading ? (
        <div className="space-y-3 max-w-3xl">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="space-y-1.5 flex-1">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-64" />
              </div>
              <Skeleton className="h-5 w-10 rounded-full ml-4" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6 max-w-3xl">
          {categories.map((category) => {
            const categoryFlags = FLAG_DEFINITIONS.filter((f) => f.category === category);
            return (
              <div key={category}>
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  {category}
                </h2>
                <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
                  {categoryFlags.map((def) => {
                    const enabled = getFlagValue(def.key);
                    const updatedAt = getFlagUpdatedAt(def.key);
                    const updatedBy = getFlagUpdatedBy(def.key);

                    return (
                      <div
                        key={def.key}
                        className="flex items-center justify-between px-5 py-4 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1 min-w-0 mr-6">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{def.label}</p>
                            {enabled ? (
                              <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700 border border-green-200 font-medium whitespace-nowrap">
                                Enabled
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-gray-400 font-medium whitespace-nowrap">
                                Disabled
                              </Badge>
                            )}
                            <Badge
                              variant="outline"
                              className={`text-[10px] px-1.5 py-0 font-medium whitespace-nowrap ${RISK_STYLES[def.risk]}`}
                            >
                              {def.risk} risk
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{def.description}</p>
                          {(updatedAt || updatedBy) && (
                            <p className="text-[10px] text-gray-300 mt-1">
                              <span className="truncate max-w-[120px] block">
                                Last changed
                                {updatedBy ? ` by ${updatedBy}` : ""}
                                {updatedAt
                                  ? ` on ${new Date(updatedAt).toLocaleDateString("en-CA", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                    })}`
                                  : ""}
                              </span>
                            </p>
                          )}
                        </div>
                        <Switch
                          checked={enabled}
                          onCheckedChange={(v) =>
                            toggleMutation.mutate({ flag: def.key, value: v })
                          }
                          disabled={toggleMutation.isPending}
                          aria-label={`Toggle ${def.label}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Card className="max-w-3xl border-amber-200 bg-amber-50">
        <CardContent className="p-4 flex items-start gap-3">
          <Zap className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            Flag changes take effect immediately for all users. High-risk flags like{" "}
            <strong>Maintenance Mode</strong> and <strong>New Registrations</strong> directly
            impact user access. Changes are logged in the audit trail.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
